import { useEffect, useRef } from "react";
import type { ReactNode } from "react";
import type { Lang } from "../types.ts";
import { t } from "../i18n/ui.ts";

export interface NavItem {
  id: string;
  icon: string;
  label: string;
  onSelect: () => void;
  /** Small right-aligned note, e.g. the signed-in account name. */
  note?: string;
}

interface Props {
  open: boolean;
  lang: Lang;
  sections: NavItem[][];
  onClose: () => void;
  /** Settings that used to sit in the page header: languages, shortcuts. */
  footer?: ReactNode;
}

/**
 * Slide-out navigation. Built on <dialog> so focus trapping, Escape handling
 * and inertness of the page behind come from the platform.
 */
export function NavDrawer({ open, lang, sections, onClose, footer }: Props) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      className="drawer"
      onClose={onClose}
      onClick={(e) => {
        // Clicks land on the backdrop when they fall outside the panel box.
        if (e.target === ref.current) onClose();
      }}
      onKeyDown={(e) => {
        // Close on Escape ourselves rather than relying on the platform's
        // default action for <dialog>, which a stray preventDefault upstream
        // can cancel. stopPropagation keeps the same Escape from also
        // reaching the page's shortcuts and exiting the view behind us.
        // No preventDefault, so the native close still runs where it works;
        // onClose is idempotent.
        if (e.key === "Escape") {
          e.stopPropagation();
          onClose();
        }
      }}
      aria-label={t(lang, "menu")}
    >
      <nav className="drawer__panel">
        <div className="drawer__head">
          <strong>{t(lang, "menu")}</strong>
          <button
            type="button"
            className="drawer__close"
            onClick={onClose}
            aria-label={t(lang, "closeMenu")}
          >
            ✕
          </button>
        </div>

        {sections.map((items, i) => (
          <ul className="drawer__list" key={i}>
            {items.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  className="drawer__item"
                  onClick={() => {
                    item.onSelect();
                    onClose();
                  }}
                >
                  <span className="drawer__icon" aria-hidden="true">{item.icon}</span>
                  <span className="drawer__label">{item.label}</span>
                  {item.note && <span className="drawer__note">{item.note}</span>}
                </button>
              </li>
            ))}
          </ul>
        ))}

        {footer && <div className="drawer__footer">{footer}</div>}
      </nav>
    </dialog>
  );
}
